import { defaultLeaveOf } from '@/constant/hr/leave-rule-catalog';
import { graphqlClient } from '@/lib/graphql-client';
import {
  dateOnlyOf,
  dateOnlyRangeClause,
  orgCalendarDate,
} from '@/lib/utils/custom-date/custom-date';
import { getEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import {
  buildGetHrProfilesForOverviewQuery,
  buildGetLeaveRequestsForOverviewQuery,
  buildGetOvertimeBalancesForOverviewQuery,
} from '../graphql/leave-overview-queries';
import { buildGetHolidaysForSheetQuery } from '../graphql/leave-sheet-queries';
import {
  GetLeaveOverviewParams,
  LeaveOverviewCategoryBreakdown,
  LeaveOverviewEmployee,
  LeaveOverviewPagingResult,
} from '../types/leave-overview.types';
import { rangesOverlap } from '../utils/date-range-overlap';
import { holidayDaysOf, leaveDaysOf } from '../utils/leave-days';
import { HrTermDayLength, isOvertimeLeave, overtimeDaysOf } from '../utils/overtime-leave';

interface HrAllotmentRaw {
  LeaveCategoryId: string;
  LeaveCategory: string;
  LeaveLimitTotalDays?: number;
  IsApplicable?: boolean;
  IsUnlimited?: boolean;
  IsDefault?: boolean;
}

interface HrProfileRaw extends HrTermDayLength {
  ItemId: string;
  UserId: string;
  HRPeriodStartDate: string;
  HRPeriodEndDate: string;
  DepartmentId?: string;
  DepartmentName?: string;
  EmploymentType?: string;
  /** Annual leave carried into the term: part of the default leave's allotment. */
  LeaveCarryForwardDays?: number;
  LeaveAllotments?: HrAllotmentRaw[];
}

interface LeaveRequestRaw {
  UserId: string;
  LeaveCategoryId: string;
  StartDate: string;
  EndDate: string;
  LeaveType?: string;
  Status: string;
}

interface OvertimeBalanceRaw {
  UserId: string;
  Source?: string | null;
  TotalRemainingOvertimeHourInSecond?: number;
}

// One query for a page of employees' leave requests (see buildGetLeaveRequestsForOverviewQuery) — a
// generous upper bound rather than a second paged round trip.
const OVERVIEW_LEAVE_REQUEST_PAGE_SIZE = 2000;
// The organization's holidays across the page's HR periods (a few a year).
const OVERVIEW_HOLIDAY_PAGE_SIZE = 1000;

/** Per-category Allocated/Taken/Requested/Remaining for one employee, same formula as the Leave
 * Sheet's module (`LeaveLimitTotalDays`, minus the `Approved`/`Pending` requests' working days inside the
 * HR period, counted now from the term's work days and `holidays`: `leaveDaysOf`), applied once per
 * `IsApplicable` category; the term's default leave (`defaultLeaveOf`) also
 * gets the annual leave carried into the term (`LeaveCarryForwardDays`). The "Overtime Leave" has
 * nothing allocated: its remaining is the employee's remaining overtime, in days of their work day
 * (legacy LeaveDetailService). */
export const buildCategoryBreakdown = (
  profile: HrProfileRaw,
  requestsForEmployee: LeaveRequestRaw[],
  remainingOvertimeSeconds: number,
  holidays: ReadonlySet<string> = new Set()
): LeaveOverviewCategoryBreakdown[] => {
  const periodStart = dateOnlyOf(profile.HRPeriodStartDate);
  const periodEnd = dateOnlyOf(profile.HRPeriodEndDate);
  const period = { start: periodStart, end: periodEnd };
  const workDays = profile.WorkDays ?? [];
  const defaultLeave = defaultLeaveOf(profile.LeaveAllotments ?? [], profile.EmploymentType);

  return (profile.LeaveAllotments ?? [])
    .filter((allotment) => allotment.IsApplicable)
    .map((allotment): LeaveOverviewCategoryBreakdown => {
      const categoryRequests = requestsForEmployee.filter(
        (request) =>
          request.LeaveCategoryId === allotment.LeaveCategoryId &&
          rangesOverlap(
            dateOnlyOf(request.StartDate),
            dateOnlyOf(request.EndDate),
            periodStart,
            periodEnd
          )
      );
      const sumWhere = (status: string) =>
        categoryRequests
          .filter((request) => request.Status === status)
          .reduce((sum, request) => sum + leaveDaysOf(request, workDays, holidays, period), 0);

      const overtimeLeave = isOvertimeLeave(allotment.LeaveCategory);
      const carryForward =
        defaultLeave?.LeaveCategoryId === allotment.LeaveCategoryId
          ? (profile.LeaveCarryForwardDays ?? 0)
          : 0;
      const allocated = overtimeLeave ? 0 : (allotment.LeaveLimitTotalDays ?? 0) + carryForward;
      const taken = sumWhere('Approved');

      return {
        LeaveCategoryId: allotment.LeaveCategoryId,
        LeaveCategory: allotment.LeaveCategory,
        IsUnlimited: !overtimeLeave && Boolean(allotment.IsUnlimited),
        IsOvertimeLeave: overtimeLeave,
        Allocated: allocated,
        Taken: taken,
        Requested: sumWhere('Pending'),
        Remaining: overtimeLeave
          ? overtimeDaysOf(
              remainingOvertimeSeconds,
              profile,
              orgCalendarDate(new Date().toISOString())
            )
          : allocated - taken,
      };
    });
};

const toEmployeeSummary = (
  profile: HrProfileRaw,
  displayName: string,
  categories: LeaveOverviewCategoryBreakdown[]
): LeaveOverviewEmployee => {
  const limitedCategories = categories.filter(
    (category) => !category.IsUnlimited && !category.IsOvertimeLeave
  );
  const sum = (pick: (category: LeaveOverviewCategoryBreakdown) => number) =>
    limitedCategories.reduce((total, category) => total + pick(category), 0);

  return {
    ItemId: profile.ItemId,
    UserId: profile.UserId,
    DisplayName: displayName,
    DepartmentId: profile.DepartmentId,
    DepartmentName: profile.DepartmentName,
    PeriodStartDate: profile.HRPeriodStartDate,
    PeriodEndDate: profile.HRPeriodEndDate,
    Categories: categories,
    TotalAllocated: sum((category) => category.Allocated),
    TotalTaken: sum((category) => category.Taken),
    TotalRequested: sum((category) => category.Requested),
    TotalRemaining: sum((category) => category.Remaining),
    UnlimitedCategoryNames: categories
      .filter((category) => category.IsUnlimited)
      .map((category) => category.LeaveCategory),
  };
};

/**
 * Fetches a page of employees' current HR terms (optionally scoped to a department), batch-fetches
 * their Approved/Pending leave requests in one query (`UserId: { $in: [...] }`), and reduces both to
 * one summary row per employee with a full per-category breakdown for the drill-down dialog.
 */
export const getLeaveOverview = async (
  params: GetLeaveOverviewParams
): Promise<LeaveOverviewPagingResult> => {
  const { pageNo, pageSize, departmentId, userIds: keywordUserIds, order } = params;

  // The keyword search box resolved to nobody — show an empty list, don't fall back to unfiltered.
  if (keywordUserIds && keywordUserIds.length === 0) {
    return { totalCount: 0, items: [] };
  }

  const where: Record<string, unknown> = { IsCurrentHrYear: true };
  if (departmentId) where.DepartmentId = departmentId;
  if (keywordUserIds) where.UserId = { $in: keywordUserIds };

  const profileResult = await graphqlClient.query<{
    HrProfiles: { totalCount: number; items: HrProfileRaw[] };
  }>({
    query: buildGetHrProfilesForOverviewQuery(where, pageNo, pageSize, order),
  });

  const profiles = profileResult.HrProfiles.items;
  if (profiles.length === 0) {
    return { totalCount: profileResult.HrProfiles.totalCount, items: [] };
  }

  const userIds = [...new Set(profiles.map((profile) => profile.UserId))];

  // One loose outer bound across this page's HR periods (normally identical for everyone on the
  // same page), refined per employee afterwards in buildCategoryBreakdown — same batch-then-refine
  // approach hr-leave-calendar already uses, no per-employee round trip.
  const periodDays = profiles.map((profile) => ({
    start: dateOnlyOf(profile.HRPeriodStartDate),
    end: dateOnlyOf(profile.HRPeriodEndDate),
  }));
  const outerStart = periodDays.reduce(
    (min, { start }) => (start < min ? start : min),
    periodDays[0].start
  );
  const outerEnd = periodDays.reduce((max, { end }) => (end > max ? end : max), periodDays[0].end);
  const outerRange = dateOnlyRangeClause(outerStart, outerEnd);

  // A page whose profiles have no readable period bounds at all has nothing to overlap-match
  // against, so the leave-request read is skipped rather than sent with an unbounded filter.
  const [leaveResult, employees, overtimeResult, holidayResult] = await Promise.all([
    outerRange
      ? graphqlClient.query<{ LeaveRequests: { items: LeaveRequestRaw[] } }>({
          query: buildGetLeaveRequestsForOverviewQuery(
            {
              UserId: { $in: userIds },
              Status: { $in: ['Approved', 'Pending'] },
              StartDate: { $lt: outerRange.$lt },
              EndDate: { $gte: outerRange.$gte },
            },
            1,
            OVERVIEW_LEAVE_REQUEST_PAGE_SIZE
          ),
        })
      : Promise.resolve({ LeaveRequests: { items: [] as LeaveRequestRaw[] } }),
    getEmployeesByUserIds(userIds),
    graphqlClient.query<{ Overtimes: { items: OvertimeBalanceRaw[] } }>({
      query: buildGetOvertimeBalancesForOverviewQuery(userIds),
    }),
    outerRange
      ? graphqlClient.query<{ Holidays: { items: { StartDate: string }[] } }>({
          query: buildGetHolidaysForSheetQuery(
            { StartDate: outerRange },
            OVERVIEW_HOLIDAY_PAGE_SIZE
          ),
        })
      : Promise.resolve({ Holidays: { items: [] as { StartDate: string }[] } }),
  ]);
  const holidays = holidayDaysOf(holidayResult.Holidays.items);

  // The balance row is the one without a Source (ledger rows carry one).
  const remainingOvertimeByUserId = new Map<string, number>();
  for (const row of overtimeResult.Overtimes.items) {
    if (!row.Source && !remainingOvertimeByUserId.has(row.UserId)) {
      remainingOvertimeByUserId.set(
        row.UserId,
        Number(row.TotalRemainingOvertimeHourInSecond ?? 0)
      );
    }
  }

  const displayNameByUserId = new Map(
    employees.map((employee) => [employee.UserId, employee.DisplayName])
  );
  const requestsByUserId = new Map<string, LeaveRequestRaw[]>();
  for (const request of leaveResult.LeaveRequests.items) {
    const list = requestsByUserId.get(request.UserId) ?? [];
    list.push(request);
    requestsByUserId.set(request.UserId, list);
  }

  const items = profiles.map((profile) => {
    const categories = buildCategoryBreakdown(
      profile,
      requestsByUserId.get(profile.UserId) ?? [],
      remainingOvertimeByUserId.get(profile.UserId) ?? 0,
      holidays
    );
    // A profile without a resolved directory entry still shows its own id rather than disappearing.
    return toEmployeeSummary(
      profile,
      displayNameByUserId.get(profile.UserId) ?? profile.UserId,
      categories
    );
  });

  return { totalCount: profileResult.HrProfiles.totalCount, items };
};
